import React, { useState, useRef, useEffect } from 'react';
import { Window } from '../Window/Window';
import styled, { keyframes, css } from 'styled-components';

const rainbowAnimation = keyframes`
  0% { background: #000080; }
  20% { background: #800080; }
  40% { background: #800000; }
  60% { background: #008000; }
  80% { background: #800080; }
  100% { background: #000080; }
`;

const animationStyle = css`
  ${rainbowAnimation} 3s infinite;
`;

const PlayerContainer = styled.div`
  background: linear-gradient(180deg, #c0c0c0 0%, #a0a0a0 100%);
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NowPlaying = styled.div`
  background: #000080;
  color: white;
  padding: 16px;
  border: 2px inset #808080;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  font-family: 'Press Start 2P', monospace;
  font-size: 12px;
  overflow: hidden;
  animation: ${props => props.isPlaying ? animationStyle : 'none'};
  transition: background 0.3s ease;

  .song-title {
    width: 100%;
    height: 1.2em;
    overflow: hidden;
    white-space: nowrap;
    position: relative;
    
    span {
      position: absolute;
      left: ${props => props.isPlaying ? '100%' : '50%'};
      transform: ${props => props.isPlaying ? 'none' : 'translateX(-50%)'};
      animation: ${props => props.isPlaying ? 'scroll 15s linear infinite' : 'none'};
      white-space: nowrap;
    }
  }

  @keyframes scroll {
    from { left: 100% }
    to { left: -100% }
  }
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  width: 100%;
  max-width: 200px;
  margin: 0 auto;
`;

const Button = styled.button`
  padding: 8px;
  background: #d4d4d4;
  border: 2px solid;
  border-color: #ffffff #808080 #808080 #ffffff;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.1s;
  
  &:active {
    border-color: #808080 #ffffff #ffffff #808080;
    transform: scale(0.95);
  }

  &:hover {
    background: #e0e0e0;
  }
`;

const IconButton = styled(Button)`
  width: 32px;
  height: 32px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 16px;
    height: 16px;
    image-rendering: pixelated;
  }
`;

const SongList = styled.div`
  flex: 1;
  overflow-y: auto;
  border: 2px inset #808080;
  background: white;
  border-radius: 4px;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.1);

  &::-webkit-scrollbar {
    width: 16px;
  }
  
  &::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-left: 1px solid #808080;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #808080;
    border: 1px solid #c0c0c0;
    border-right: 1px solid #000;
    border-bottom: 1px solid #000;
  }
`;

const SongItem = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #d3d3d3;
  transition: all 0.1s;
  
  &:hover {
    background: #000080;
    color: white;
  }

  .song-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    
    .title {
      font-weight: bold;
    }
    
    .artist {
      font-size: 0.9em;
      color: ${props => props.isPlaying ? '#4040ff' : '#666'};
    }
  }
`;

const PlaybackInfo = styled.div`
  background: #000080;
  color: white;
  padding: 8px;
  font-family: 'Press Start 2P', monospace;
  font-size: 12px;
  text-align: center;
  border: 2px inset #808080;
  animation: ${props => props.isPlaying ? animationStyle : 'none'};
  transition: background 0.3s ease;
`;

const VolumeControl = styled.div`
  background: #000080;
  padding: 8px;
  border: 2px inset #808080;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  animation: ${props => props.isPlaying ? animationStyle : 'none'};
  transition: background 0.3s ease;

  input[type="range"] {
    flex: 1;
    height: 16px;
    -webkit-appearance: none;
    background: #c0c0c0;
    border: 2px inset #808080;
    position: relative;
    
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 8px;
      height: 16px;
      background: #c0c0c0;
      border: 1px outset #ffffff;
      border-radius: 0;
      cursor: pointer;
      position: relative;
      z-index: 1;
    }

    &::-webkit-slider-runnable-track {
      height: 100%;
      border: none;
    }
  }
`;

const VolumeIcon = styled.img`
  width: 16px;
  height: 16px;
  image-rendering: pixelated;
  margin-left: 4px;
  filter: invert(1);
`;

const PlayButton = styled(IconButton)`
  width: 24px;
  height: 24px;
  padding: 2px;
  background: #c0c0c0;
  border: 2px outset #ffffff;

  &:active {
    border-style: inset;
  }
`;

export const MP3Player = ({ onClose, ...props }) => {
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.2);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  // Clean up function for audio
  const cleanupAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.load();
    }
  };

  // Handle window close
  const handleClose = () => {
    cleanupAudio();
    onClose();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, []);

  // Time update effect
  useEffect(() => {
    const audio = audioRef.current;
    
    const updateTime = () => {
      if (audio) {
        setCurrentTime(audio.currentTime);
      }
    };

    if (audio) {
      audio.addEventListener('timeupdate', updateTime);
    }

    return () => {
      if (audio) {
        audio.removeEventListener('timeupdate', updateTime);
      }
    };
  }, []);

  // Add songs array
  const songs = [
    {
      id: 1,
      title: "notepad tutorial",
      artist: "dj dOS",
      url: "/music/notepadtutorial.mp3"  // Path relative to public folder
    },
    {
      id: 2,
      title: "calming sounds",
      artist: "bird",
      url: "/music/calmingsounds.mp3"  // Replace with your actual song file path
    },
    {
      id: 3,
      title: "captain jack",
      artist: "some legend",
      url: "/music/captainjack.mp3"  // Replace with your actual song file path
    }
  ];

  // Set initial volume when audio element is created
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [audioRef, volume]);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const getVolumeIcon = () => {
    if (volume === 0) return '/images/icons/media/volume-mute.svg';
    if (volume < 0.5) return '/images/icons/media/volume-low.svg';
    return '/images/icons/media/volume-high.svg';
  };

  const playSong = (song) => {
    setCurrentSong(song);
    if (audioRef.current) {
      audioRef.current.src = song.url;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const togglePlay = () => {
    if (!currentSong && songs.length > 0) {
      // If no song is playing, play the first song
      playSong(songs[0]);
    } else if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const nextSong = () => {
    if (currentSong && songs.length > 0) {
      const currentIndex = songs.findIndex(song => song.id === currentSong.id);
      const nextIndex = (currentIndex + 1) % songs.length;
      playSong(songs[nextIndex]);
    }
  };

  const previousSong = () => {
    if (currentSong && songs.length > 0) {
      const currentIndex = songs.findIndex(song => song.id === currentSong.id);
      const prevIndex = (currentIndex - 1 + songs.length) % songs.length;
      playSong(songs[prevIndex]);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Expose cleanupAudio function through a ref
  React.useImperativeHandle(props.mp3Ref, () => ({
    cleanup: cleanupAudio
  }));

  return (
    <Window
      title="dOS MP3 Player"
      onClose={handleClose}
      isActive={props.isActive}
      onActivate={props.onActivate}
      isMinimized={props.isMinimized}
      isMaximized={props.isMaximized}
      onMinimize={props.onMinimize}
      onMaximize={props.onMaximize}
      defaultWidth="280px"    // Made narrower
      defaultHeight="460 px"   // Made taller
      initialPosition={props.initialPosition}
    >
      <PlayerContainer>
        <audio ref={audioRef} onEnded={nextSong} />
        
        <NowPlaying isPlaying={isPlaying}>
          <div className="song-title">
            <span>
              {currentSong ? currentSong.title : 'No song playing'}
            </span>
          </div>
          <Controls>
            <IconButton onClick={previousSong}>
              <img src="/images/icons/media/prev.svg" alt="Previous" />
            </IconButton>
            <IconButton onClick={togglePlay}>
              <img 
                src={isPlaying ? '/images/icons/media/pause.svg' : '/images/icons/media/play.svg'} 
                alt={isPlaying ? 'Pause' : 'Play'} 
              />
            </IconButton>
            <IconButton onClick={nextSong}>
              <img src="/images/icons/media/next.svg" alt="Next" />
            </IconButton>
          </Controls>
        </NowPlaying>

        <PlaybackInfo isPlaying={isPlaying}>
          {isPlaying ? 'NOW PLAYING' : 'PAUSED'} - {formatTime(currentTime)}
        </PlaybackInfo>

        <SongList>
          {songs.map(song => (
            <SongItem key={song.id} isPlaying={currentSong?.id === song.id}>
              <div className="song-info">
                <span className="title">{song.title}</span>
                <span className="artist">{song.artist}</span>
              </div>
              <PlayButton onClick={() => playSong(song)}>
                <img src="/images/icons/media/play.svg" alt="Play" />
              </PlayButton>
            </SongItem>
          ))}
        </SongList>

        <VolumeControl isPlaying={isPlaying}>
          <VolumeIcon src={getVolumeIcon()} alt="Volume" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
          />
        </VolumeControl>
      </PlayerContainer>
    </Window>
  );
}; 